import { normalizeOptimizationData, hasMethodFrequencyData, METHOD_KEYS, } from "@/utils/optimizationData";
import { logger } from "@/utils/logger";
import { buildFrequencyRequestBody, extractPolicyPayload, parseVehicleCatalog, policyOf, } from "./frequencyPolicy";
import { extractSenaryoYaniti } from "@/composables/frequency/hatOzeti.js";
function loadClient() {
    return import("./client").then((mod) => mod.default);
}
const api = {
    get(url, config) {
        return loadClient().then((client) => client.get(url, config));
    },
    delete(url, config) {
        return loadClient().then((client) => client.delete(url, config));
    },
    post(url, data, config) {
        return loadClient().then((client) => client.post(url, data, config));
    },
    put(url, data, config) {
        return loadClient().then((client) => client.put(url, data, config));
    },
    patch(url, data, config) {
        return loadClient().then((client) => client.patch(url, data, config));
    },
};
function emptyOptimizationGrids() {
    const blank = () => Object.fromEntries(METHOD_KEYS.map((key) => [key, []]));
    return { gidis: blank(), donus: blank(), seferler: blank() };
}
async function catalogForRequest(params) {
    if (Array.isArray(params.vehicleCatalog))
        return params.vehicleCatalog;
    if (policyOf(params) === "classic" && typeof params.otomatik !== "boolean")
        return undefined;
    try {
        const response = await api.get("/global-settings/");
        return parseVehicleCatalog(response.data);
    }
    catch (error) {
        logger.warn("Araç kataloğu okunamadı:", error);
        return undefined;
    }
}
const frequencyApi = {
    fetchVehicleCatalog() {
        return api.get("/global-settings/").then((response) => parseVehicleCatalog(response.data));
    },
    async analyze(params) {
        const catalog = await catalogForRequest(params);
        const built = buildFrequencyRequestBody(params, catalog);
        if (!built.ok || !built.body) {
            throw new Error(built.message || "Frekans isteği gönderilmedi");
        }
        const wire = built.body;
        logger.debug("Tam analiz isteği gönderiliyor:", wire);
        return Promise.all([
            api.post("/tam-analiz", wire),
            api.post("/frekans-optimizasyon", wire),
            api.get("/parkur-sureleri", {
                params: {
                    hatNo: wire.hatNo,
                    gun: wire.gun,
                },
            }),
        ]).then(([tamAnalizResponse, optimizasyonResponse, parkurResponse]) => {
            let parkurData;
            try {
                const jsonStr = typeof parkurResponse.data === "string"
                    ? parkurResponse.data.replace(/: NaN/g, ": null")
                    : JSON.stringify(parkurResponse.data).replace(/: NaN/g, ": null");
                const rawData = JSON.parse(jsonStr);
                let formattedParkurSureleri = {
                    gidis: {},
                    donus: {},
                };
                if (Array.isArray(rawData.parkur_sureleri)) {
                    logger.debug("Parkur süreleri array formatında, dönüştürülüyor...");
                    rawData.parkur_sureleri.forEach((item) => {
                        const yon = item.Yon === "Gidiş" ? "gidis" : "donus";
                        if (item.Saat !== null && item.Saat !== undefined) {
                            const saatKey = `${String(item.Saat).padStart(2, "0")}_0`;
                            formattedParkurSureleri[yon][saatKey] = item.Parkur_Suresi ?? null;
                        }
                    });
                    logger.debug("Dönüştürülen parkur süreleri:", formattedParkurSureleri);
                }
                else {
                    logger.debug("Parkur süreleri zaten doğru formatta");
                    const existing = rawData.parkur_sureleri;
                    formattedParkurSureleri = existing || {
                        gidis: {},
                        donus: {},
                    };
                }
                parkurData = {
                    ...rawData,
                    parkur_sureleri: formattedParkurSureleri,
                };
            }
            catch (e) {
                logger.error("Parkur süreleri parse hatası:", e);
                throw new Error("Parkur süreleri formatı hatalı");
            }
            const parkurSureleri = parkurData.parkur_sureleri;
            logger.debug("Alınan parkur süreleri:", parkurSureleri);
            if (parkurSureleri?.gidis) {
                const gidisSureleri = Object.entries(parkurSureleri.gidis).slice(0, 3);
                logger.debug("Örnek gidiş parkur süreleri:", gidisSureleri);
            }
            if (parkurSureleri?.donus) {
                const donusSureleri = Object.entries(parkurSureleri.donus).slice(0, 3);
                logger.debug("Örnek dönüş parkur süreleri:", donusSureleri);
            }
            const errors = [];
            if (!tamAnalizResponse.data.success)
                errors.push("Tam analiz başarısız");
            if (!optimizasyonResponse.data.success) {
                logger.warn("Frekans optimizasyon endpoint başarısız; tam analiz yedek verisi deneniyor:", optimizasyonResponse.data?.error);
            }
            if (!parkurSureleri ||
                (Object.keys(parkurSureleri.gidis || {}).length === 0 &&
                    Object.keys(parkurSureleri.donus || {}).length === 0)) {
                errors.push("Parkur süreleri alınamadı");
            }
            const normalizedOptimization = normalizeOptimizationData(optimizasyonResponse.data.sonuclar, tamAnalizResponse.data.sonuclar);
            if (!normalizedOptimization &&
                !optimizasyonResponse.data.success &&
                !hasMethodFrequencyData(tamAnalizResponse.data.sonuclar)) {
                errors.push(optimizasyonResponse.data?.error || "Optimizasyon verisi alınamadı");
            }
            const optimizationPreview = normalizedOptimization || emptyOptimizationGrids();
            if (errors.length > 0) {
                throw new Error(errors.join(", "));
            }
            const tamSonuclar = tamAnalizResponse.data.sonuclar || {};
            const gidisBase = tamSonuclar.gidis || {};
            const donusBase = tamSonuclar.donus || {};
            const combinedData = {
                ...tamSonuclar,
                gidis: {
                    ...gidisBase,
                    parkur_suresi: parkurSureleri.gidis,
                },
                donus: {
                    ...donusBase,
                    parkur_suresi: parkurSureleri.donus,
                },
                aracTipi: wire.aracTipi ?? params.aracTipi,
            };
            logger.debug("Vuex'e gönderilecek veri yapısı:", {
                "gidis.parkur_suresi var mı": !!(combinedData.gidis && combinedData.gidis.parkur_suresi),
                "donus.parkur_suresi var mı": !!(combinedData.donus && combinedData.donus.parkur_suresi),
                "gidis.parkur_suresi örnek anahtarlar": Object.keys(combinedData.gidis?.parkur_suresi || {}).slice(0, 3),
                "donus.parkur_suresi örnek anahtarlar": Object.keys(combinedData.donus?.parkur_suresi || {}).slice(0, 3),
            });
            if (Object.keys(parkurSureleri.gidis || {}).length === 0) {
                logger.warn("DİKKAT: Gidiş parkur süreleri boş!");
            }
            if (Object.keys(parkurSureleri.donus || {}).length === 0) {
                logger.warn("DİKKAT: Dönüş parkur süreleri boş!");
            }
            const typeOutcome = extractPolicyPayload(optimizasyonResponse.data) ||
                extractPolicyPayload(tamAnalizResponse.data?.sonuclar);
            const senaryoYaniti = extractSenaryoYaniti(optimizasyonResponse.data);
            return {
                data: combinedData,
                optimization: typeOutcome
                    ? { ...optimizationPreview, typeOutcome }
                    : optimizationPreview,
                typeOutcome,
                senaryoYaniti,
            };
        });
    },
    loadPlan(planId) {
        return api.get(`/plans/${planId}`);
    },
    savePlan(planData) {
        return api.post("/plans", planData);
    },
    exportToDatabase(data) {
        const payload = {
            servis_verileri: data.servis_verileri,
            hat_no: data.hat_no,
            gun_tipi: data.gun_tipi,
            sil_mevcut: data.sil_mevcut === true,
        };
        if (data.metod)
            payload.metod = data.metod;
        if (data.metod_key)
            payload.metod_key = data.metod_key;
        if (data.beta != null)
            payload.beta = data.beta;
        if (data.senaryo_id != null)
            payload.senaryo_id = data.senaryo_id;
        logger.debug("Veritabanına aktarım isteği gönderiliyor:", payload);
        return api.post("/export-to-database", payload);
    },
    getHatList(params) {
        logger.debug("Hat listesi sorgulanıyor:", params);
        return api.get("/hat-listesi", { params });
    },
    getSqlHatList(params = {}) {
        logger.debug("SQL hat listesi sorgulanıyor (dim_ubs_hatlar):", params);
        return api.get("/sql-hat-listesi", { params });
    },
    getHatDuraklari(params) {
        logger.debug("Hat durakları sorgulanıyor (dim_ubs_hat_duraklari):", params);
        return api.get("/hat-duraklari", {
            params: {
                hatNo: params.hatNo ?? params.hat_no,
            },
        });
    },
    getPlanList(params) {
        logger.debug("Plan listesi sorgulanıyor:", params);
        return api.get("/get-plan-list", { params });
    },
    getPlanDetail(params) {
        logger.debug("Plan detayı sorgulanıyor:", params);
        return api.get("/get-plan-detail", { params });
    },
    topluFrekansOptimizasyon(params) {
        logger.debug("Toplu frekans optimizasyon isteği gönderiliyor:", params);
        return api.post("/toplu-frekans-optimizasyon", params).then((response) => {
            const body = response.data;
            if (!body.success) {
                throw new Error(body.error || "Toplu frekans optimizasyonu sırasında bir hata oluştu");
            }
            const sonuclar = body.sonuclar || {};
            logger.debug("Toplu frekans optimizasyon sonuçları:", {
                "Özet var mı": !!sonuclar.ozet,
                "Detaylı sonuçlar sayısı": Array.isArray(sonuclar.detayli_sonuclar)
                    ? sonuclar.detayli_sonuclar.length
                    : 0,
                "Frekans tablosu sayısı": Array.isArray(sonuclar.frekans_tablosu)
                    ? sonuclar.frekans_tablosu.length
                    : 0,
                "Araç planları sayısı": Array.isArray(sonuclar.arac_planlari)
                    ? sonuclar.arac_planlari.length
                    : 0,
                "Hata raporu sayısı": Array.isArray(sonuclar.hata_raporu)
                    ? sonuclar.hata_raporu.length
                    : 0,
                "Servis saatleri sayısı": Array.isArray(sonuclar.servis_saatleri)
                    ? sonuclar.servis_saatleri.length
                    : 0,
                "Senkronizasyon raporu sayısı": Array.isArray(sonuclar.senkronizasyon_raporu)
                    ? sonuclar.senkronizasyon_raporu.length
                    : 0,
                "Senkronizasyon aktif": sonuclar.ozet
                    ? sonuclar.ozet.senkronizasyon_aktif
                    : false,
                "Senkronizasyon uygulanan hat sayısı": sonuclar.ozet
                    ? sonuclar.ozet.senkronizasyon_uygulanan
                    : 0,
            });
            return sonuclar;
        });
    },
    listFrekansHatPresets(params = {}) {
        return api.get("/frekans-hat-presets", { params }).then((res) => res.data);
    },
    getFrekansHatPreset(presetId) {
        return api.get(`/frekans-hat-presets/${presetId}`).then((res) => res.data);
    },
    createFrekansHatPreset(payload) {
        return api.post("/frekans-hat-presets", payload).then((res) => res.data);
    },
    updateFrekansHatPreset(presetId, payload) {
        return api.put(`/frekans-hat-presets/${presetId}`, payload).then((res) => res.data);
    },
    deleteFrekansHatPreset(presetId) {
        return api.delete(`/frekans-hat-presets/${presetId}`).then((res) => res.data);
    },
    pathCoverAtama(payload) {
        return api
            .post("/frekans-path-cover-atama", payload)
            .then((res) => res.data);
    },
    startNetworkRun(body) {
        return api
            .post("/frekans-ag-kosusu", body)
            .then((res) => ({ status: res.status, data: res.data }));
    },
    getNetworkRun(id, params = {}) {
        return api
            .get(`/frekans-ag-kosusu/${id}`, { params })
            .then((res) => res.data);
    },
    getNetworkRunHat(id, hatNo) {
        return api
            .get(`/frekans-ag-kosusu/${id}/hat/${encodeURIComponent(hatNo)}`)
            .then((res) => res.data);
    },
    stopNetworkRun(id) {
        return api
            .post(`/frekans-ag-kosusu/${id}/durdur`)
            .then((res) => res.data);
    },
    retryNetworkRun(id) {
        return api
            .post(`/frekans-ag-kosusu/${id}/tekrar`)
            .then((res) => res.data);
    },
    patchNetworkRunHatDecision(id, hatNo, body) {
        return api
            .patch(`/frekans-ag-kosusu/${id}/hat/${encodeURIComponent(hatNo)}/karar`, body)
            .then((res) => res.data);
    },
    bulkApproveNetworkRunHats(id, hatlar) {
        return api
            .post(`/frekans-ag-kosusu/${id}/onay-toplu`, { hatlar })
            .then((res) => res.data);
    },
    publishNetworkRunApproved(id, senaryoId) {
        return api
            .post(`/frekans-ag-kosusu/${id}/yayinla`, { senaryo_id: senaryoId })
            .then((res) => res.data);
    },
    listNetworkRuns() {
        return api
            .get("/frekans-ag-kosulari")
            .then((res) => res.data);
    },
    /** Senaryo SSD: hat_no → son olusturma_tarihi ISO */
    fetchSenaryoHatKayitlari(senaryoId) {
        return api
            .get("/frekans-senaryo-hat-kayitlari", { params: { senaryo_id: senaryoId } })
            .then((res) => res.data);
    },
};
export default frequencyApi;
